import type { ListData } from '@bluevision/tools';
import type { ValueOf } from 'type-fest';

export namespace IMediaManagement {
  export namespace Common {
    export enum OperateMode {
      ADD = 'ADD',
      EDIT = 'EDIT',
    }

    export enum MediaFlagEnum {
      YES = 'Yes',
      NO = 'No',
    }

    /** 表单状态: 全部 未配置 草稿 已发布 */
    export const MediaFormStatus = {
      UN_CONFIGURED: 'unconfigured',
      DRAFT: 'draft',
      PUBLISHED: 'published',
    } as const;
    export const MediaFormStatusMap = {
      [MediaFormStatus.UN_CONFIGURED]: '未配置',
      [MediaFormStatus.DRAFT]: '草稿',
      [MediaFormStatus.PUBLISHED]: '已发布',
    };
    export const MediaFormStatusColorMap = {
      [MediaFormStatus.UN_CONFIGURED]: 'gray',
      [MediaFormStatus.DRAFT]: 'red',
      [MediaFormStatus.PUBLISHED]: 'green',
    };

    // 媒体下拉列表
    export interface MediaMetaInfo {
      /** 媒体的唯一标识符 */
      id: number;
      /** 媒体名称 */
      media_name: string;
      /** 媒体英文名称 */
      media_name_en: string;
      /** 媒体简介 */
      media_profile: string;
      /** 是否自建账户 */
      with_self_made_account: IMediaManagement.Common.MediaFlagEnum;
      /** 是否无上限 */
      unlimited: IMediaManagement.Common.MediaFlagEnum;
      /** 是否带首充 */
      with_first_recharge: IMediaManagement.Common.MediaFlagEnum;
      /** 首次充值门槛 */
      first_recharge_threshold: number;
      /** 单次最大开户量 */
      max_amount_of_account_opening: number;
      /** 是否必需媒体资产 */
      media_asset_required: IMediaManagement.Common.MediaFlagEnum;
      /** 媒体资产名称 */
      media_asset_name: string[];
      /** 是否有API */
      with_api: IMediaManagement.Common.MediaFlagEnum;
      /** 媒体原有数字映射 */
      media_key: number;
      /** 媒体原有字符串映射 */
      media_key_value: string;
      /** 是否新兴媒体 */
      new_media: IMediaManagement.Common.MediaFlagEnum;
      /** 是否可编辑 */
      editable: IMediaManagement.Common.MediaFlagEnum;
      /** 币种 */
      currency: string;
      /** 是否支持开户 */
      support_account_open: IMediaManagement.Common.MediaFlagEnum;
      /** 表单状态 */
      form_status: ValueOf<typeof IMediaManagement.Common.MediaFormStatus>;
      media_logo?: string;
    }
  }

  export namespace Request {
    export interface GetMediaManagementList {
      with_first_recharge?: IMediaManagement.Common.MediaFlagEnum; // 是否带首充 Yes / No
      media_name?: string; // media_name
      media_key_array?: string[];
      unlimited?: IMediaManagement.Common.MediaFlagEnum; // 是否无上限 Yes / No

      /** 表单状态: 全部 未配置 草稿 已发布 */
      status?: ValueOf<typeof IMediaManagement.Common.MediaFormStatus>;

      current_page?: number; // 页码
      page_size?: number; // 每页条数

      sort_by?: string;
      order?: string;
    }

    export interface AddMediaManagement {
      media_name: string; // 媒体名称
      media_profile: string; // 媒体简介
      with_self_made_account: IMediaManagement.Common.MediaFlagEnum; // 是否自建账户 ("Yes" 或 "No")
      unlimited: IMediaManagement.Common.MediaFlagEnum; // 是否无上限 ("Yes" 或 "No")
      with_first_recharge: IMediaManagement.Common.MediaFlagEnum; // 是否带首充 ("Yes" 或 "No")
      first_recharge_threshold: number; // 首次充值门槛
      max_amount_of_account_opening: number; // 单次最大开户量
      media_asset_required: IMediaManagement.Common.MediaFlagEnum; // 是否必需媒体资产 ("Yes" 或 "No")
      media_asset_name: string[]; // 媒体资产名称
      with_api: IMediaManagement.Common.MediaFlagEnum; // 是否有API ("Yes" 或 "No")
      support_account_open: IMediaManagement.Common.MediaFlagEnum; // 是否支持AE在新兴媒体开户 ("Yes" 或 "No")
    }

    // 媒体下拉列表
    export interface GetMediaLists {
      media_name?: string; // 媒体名称
      with_self_made_account?: IMediaManagement.Common.MediaFlagEnum; // 是否自建账户
      unlimited?: IMediaManagement.Common.MediaFlagEnum; // 是否无上限
      with_first_recharge?: IMediaManagement.Common.MediaFlagEnum; // 是否带首充
      media_asset_required?: IMediaManagement.Common.MediaFlagEnum; // 是否必需媒体资产
      with_api?: IMediaManagement.Common.MediaFlagEnum; // 是否有API
      new_media?: IMediaManagement.Common.MediaFlagEnum; // 是否新兴媒体
      media_key?: number; // 原媒体key
      media_key_value?: string; // 原媒体value en
    }
  }

  export namespace Response {
    // 列表
    export type GetMediaManagementList = ListData<IMediaManagement.Common.MediaMetaInfo>;

    // 详情
    export type MediaManagementDetail = {
      records: {
        id: number;
        action: string; // 操作类型
        operator: string; // 操作人邮箱
        owner_name: string; // 操作人
        notes: object; // 变更明细
        change_field: string[]; // 变更字段
        create_time: string;
      }[];
    } & IMediaManagement.Common.MediaMetaInfo & {
        with_apply: boolean; // 是否已经存在开户申请
      };

    // 检查媒体是否已存在
    export interface CheckMediaManagement {
      is_exist: boolean; // 是否存在
      media_id: number; // 媒体ID
    }
    export type GetMediaLists = IMediaManagement.Common.MediaMetaInfo[];
  }
}
